import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import customerLocationsApi from "./api/customerLocationsApi";
import type { CreateLocationViewModel, ChangeLocationViewModel } from "./api/customerLocationsApi";
import type { Location } from "./components/locations/locationTypes";

// =====================================================================
//  STATE
// =====================================================================

export interface CustomerLocationsState {
  locations: Location[];
  loading: boolean;
  error: string | null;
}

const initialState: CustomerLocationsState = {
  locations: [],
  loading: false,
  error: null,
};

// =====================================================================
//  THUNKS
// =====================================================================

export const fetchLocations = createAsyncThunk(
  "customerLocations/fetchLocations",
  async () => {
    return customerLocationsApi.getLocations();
  }
);

export const createLocation = createAsyncThunk(
  "customerLocations/createLocation",
  async (data: CreateLocationViewModel) => {
    await customerLocationsApi.createLocation(data);
  }
);

export const updateLocation = createAsyncThunk(
  "customerLocations/updateLocation",
  async ({ id, data }: { id: string; data: ChangeLocationViewModel }) => {
    await customerLocationsApi.updateLocation(id, data);
  }
);

export const disableLocation = createAsyncThunk(
  "customerLocations/disableLocation",
  async (id: string) => {
    await customerLocationsApi.disableLocation(id);
  }
);

export const enableLocation = createAsyncThunk(
  "customerLocations/enableLocation",
  async (id: string) => {
    await customerLocationsApi.enableLocation(id);
  }
);

export const setDefaultLocation = createAsyncThunk(
  "customerLocations/setDefaultLocation",
  async (id: string) => {
    await customerLocationsApi.setDefaultLocation(id);
  }
);

// LOC-DELETE (§7.1) — permanent hard delete. The API resolves 409 (blocked /
// unverified) as a normal response, so the discriminated union rides through as
// the FULFILLED payload; only 403/404/5xx reject. Same additive pattern as
// enableLocation (no state mutation on success — the page refetches).
export const deleteLocationPermanently = createAsyncThunk(
  "customerLocations/deleteLocationPermanently",
  async (id: string) => {
    return customerLocationsApi.deleteLocationPermanently(id);
  }
);

// =====================================================================
//  SLICE
// =====================================================================

const customerLocationsSlice = createSlice({
  name: "customerLocations",
  initialState,
  reducers: {
    clearLocationsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch
    builder
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLocations.fulfilled, (state, { payload }) => {
        state.locations = payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchLocations.rejected, (state, { error }) => {
        state.loading = false;
        state.error = error.message || "Failed to load locations";
      });

    // Create
    builder
      .addCase(createLocation.rejected, (state, { error }) => {
        state.error = error.message || "Failed to create location";
      });

    // Update
    builder
      .addCase(updateLocation.rejected, (state, { error }) => {
        state.error = error.message || "Failed to update location";
      });

    // Disable
    builder
      .addCase(disableLocation.rejected, (state, { error }) => {
        state.error = error.message || "Failed to disable location";
      });

    // Enable
    builder
      .addCase(enableLocation.rejected, (state, { error }) => {
        state.error = error.message || "Failed to enable location";
      });

    // Set default
    builder
      .addCase(setDefaultLocation.rejected, (state, { error }) => {
        state.error = error.message || "Failed to set default location";
      });

    // Permanent delete (LOC-DELETE) — only 403/404/5xx reach here (409 is a
    // fulfilled union payload handled at the page).
    builder
      .addCase(deleteLocationPermanently.rejected, (state, { error }) => {
        state.error = error.message || "Failed to delete location";
      });
  },
});

export const { clearLocationsError } = customerLocationsSlice.actions;

export default customerLocationsSlice.reducer;
